import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { environment } from '@env/environment';

export interface AppStartupModel {
    languages?: LanguageViewModel[];
    professions?: ProfessionLookupViewModel[];
    countries?: CountryViewModel[];
    versionDetails?: MobileAppVersionViewModel;
    minimumVersion?: number;
}

export interface LanguageViewModel {
    id: string;
    isO1: string;
    name?: string;
}

export interface ProfessionLookupViewModel {
    id?: string;
    name?: string;
}

export interface CountryViewModel {
    id?: string;
    name?: string;
    code?: string;
}

export interface MobileAppVersionViewModel {
    version?: string;
    buildNumber?: number;
}

export interface AppStartupModelResponseResult {
    isSuccess: boolean;
    statusCode: number;
    result: AppStartupModel;
    errors?: any[];
    version?: string;
    environment?: string;
}

@Injectable({
    providedIn: 'root',
})
export class AppInitializerService {
    private readonly http = inject(HttpClient);
    private readonly translateService = inject(TranslateService);

    private _appStartupData: AppStartupModel | undefined;
    private initializationSubject = new ReplaySubject<boolean>(1);
    private initializationComplete = false;

    async initializeApp(): Promise<boolean> {
        if (this.initializationComplete) {
            return true;
        }
        return new Promise<boolean>((resolve) => {
            if (!this.initializationSubject.observed) {
                this.performAppStartupInitialization();
            }

            this.initializationSubject.subscribe((success) => {
                this.initializationComplete = true;
                resolve(success);
            });
        });
    }

    private performAppStartupInitialization(): void {
        const url = `${environment.apiBaseUrl}/v4.0/lookup/app-startup`;

        firstValueFrom(
            this.http.get<AppStartupModelResponseResult>(url).pipe(
                catchError((error) => {
                    console.error('Error fetching app startup data:', error);
                    return of(null);
                }),
                tap((response) => {
                    if (response && response.result) {
                        this.configureAppStartupData(response.result);
                    } else {
                        this.setFallbackAppStartupData();
                    }
                }),
                finalize(() => {
                    this.initializationSubject.next(true);
                    this.initializationSubject.complete();
                })
            )
        );
    }

    private configureAppStartupData(data: AppStartupModel): void {
        this._appStartupData = data;
    }

    private setFallbackAppStartupData(): void {
        this._appStartupData = undefined;
    }

    get appStartupData(): AppStartupModel | undefined {
        return this._appStartupData;
    }
}
